import React, { useState, useCallback, useMemo, useReducer } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Science as TestIcon,
  PlayArrow as TriggerIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import { RuleType } from '../../../types/backend';
import type { PreviewRuleOutputResponse, NotificationTarget } from '../../../types/backend';
import { useEventManagementService } from '../services/eventManagementService';
import { useRuleSampleEvent } from '../hooks/useRuleSampleEvent';
import { usePatternTest } from '../hooks/usePatternTest';
import FormActionBar from '../../../shared/components/FormActionBar';
import { StepHeader, StepBox } from '../../../shared/components/StepHeader';
import type {
  EventRuleExtended,
  RuleTemplate,
  LiveEvent,
  RuleCreateExtended,
} from '../../../types/frontend';
import {
  formReducer,
  buildInitialForm,
  validateRuleForm,
} from '../utils/ruleFormUtils';
import type { RuleFormState } from '../utils/ruleFormUtils';
import { buildPresetCron } from '../utils/scheduleUtils';
import RuleScheduleSection from './RuleScheduleSection';
import RuleTargetSection from './RuleTargetSection';
import IncidentLifecycleSection from './IncidentLifecycleSection';
import TestEventEditor from './TestEventEditor';
import PatternTestResult from './PatternTestResult';
import PreviewRail from './PreviewRail';

interface RuleEditorProps {
  customerId: string;
  rule: EventRuleExtended | null;
  template: RuleTemplate | null;
  fromEvent: LiveEvent | null;
  onSaved: (savedRule: EventRuleExtended) => void;
  onDelete?: () => void;
  onDiscard: () => void;
}

const RuleEditor: React.FC<RuleEditorProps> = ({
  customerId,
  rule,
  template,
  fromEvent,
  onSaved,
  onDelete,
  onDiscard,
}) => {
  const eventService = useEventManagementService();

  const [form, dispatch] = useReducer(formReducer, null, () => buildInitialForm(rule, template, fromEvent));

  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [pendingOverwrite, setPendingOverwrite] = useState<{ periodLabel: string } | null>(null);
  const { runTest, testing, result: testResult, error: testError, setError: setTestError } = usePatternTest(customerId);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [patternValid, setPatternValid] = useState(true);
  const [testEventValid, setTestEventValid] = useState(true);

  const validationErrors = useMemo(() => validateRuleForm(form), [form]);

  const setField = useCallback(<K extends keyof RuleFormState>(field: K, value: RuleFormState[K]) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const handleTestPattern = () => runTest(form.eventPattern, form.testEvent);

  const onSampleEvent = useCallback((event: Record<string, unknown>) => setField('testEvent', event), [setField]);
  const { loadLatest, loading: loadingSample } = useRuleSampleEvent(
    customerId, rule?.rule_id ?? null, onSampleEvent, setTestError,
  );
  const handleLoadLatestEvent = useCallback(() => {
    setTestError(null);
    return loadLatest();
  }, [loadLatest, setTestError]);

  const handlePreview = useCallback(async (): Promise<PreviewRuleOutputResponse> => {
    let resolvedExpression: string | undefined;
    if (form.triggerType === 'schedule') {
      if (form.useCron) {
        const expr = form.cronExpression.trim();
        resolvedExpression = /^(cron|rate)\(.+\)$/.test(expr) ? expr : `cron(${expr})`;
      } else {
        resolvedExpression = buildPresetCron(form.schedulePreset, form.scheduleHour, form.scheduleMinute);
      }
    }

    const ruleConfig = {
      rule_type: form.automation !== null ? RuleType.AUTOMATION : RuleType.ALERT,
      name: form.name.trim() || 'preview',
      description: form.description.trim(),
      trigger_type: form.triggerType,
      event_pattern: form.triggerType === 'event' ? form.eventPattern : undefined,
      schedule_expression: resolvedExpression,
      enrichment_prompt_id: form.enrichmentPromptId ?? undefined,
      summarize: form.summarize,
      itsm: form.itsm ?? undefined,
      notification_targets: form.notificationTargets.filter((t) => t.channel !== 'webhook'),
      automation: form.automation ?? undefined,
      resolve_signal: form.resolveSignal ?? undefined,
    };

    return eventService.previewRuleOutput({
      customer_id: customerId,
      rule: ruleConfig as Record<string, unknown>,
      sample_event: form.testEvent,
    });
  }, [customerId, form, eventService]);

  const handleTriggerNow = useCallback(async () => {
    if (!rule) return;
    setTriggering(true);
    setTriggerSuccess(false);
    setError(null);
    try {
      const result = await eventService.triggerRuleNow(customerId, rule.rule_id);
      if (result.already_exists) {
        setPendingOverwrite({ periodLabel: result.period_label ?? 'this period' });
        return;
      }
      setTriggerSuccess(true);
      setTimeout(() => setTriggerSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger rule');
    } finally {
      setTriggering(false);
    }
  }, [customerId, rule, eventService]);

  const handleConfirmOverwrite = useCallback(async () => {
    if (!rule) return;
    setPendingOverwrite(null);
    setTriggering(true);
    setTriggerSuccess(false);
    setError(null);
    try {
      await eventService.triggerRuleNow(customerId, rule.rule_id, true);
      setTriggerSuccess(true);
      setTimeout(() => setTriggerSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate report');
    } finally {
      setTriggering(false);
    }
  }, [customerId, rule, eventService]);

  const handleSave = useCallback(async () => {
    setHasAttemptedSave(true);
    const problems = Object.values(validateRuleForm(form));
    if (!patternValid) problems.unshift('Event pattern is not valid JSON');
    if (problems.length > 0) {
      setError(`Fix before saving — ${problems.join('; ')}`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let resolvedExpression: string | undefined;
      if (form.triggerType === 'schedule') {
        if (form.useCron) {
          const expr = form.cronExpression.trim();
          resolvedExpression = /^(cron|rate)\(.+\)$/.test(expr) ? expr : `cron(${expr})`;
        } else {
          resolvedExpression = buildPresetCron(
            form.schedulePreset,
            form.scheduleHour,
            form.scheduleMinute,
          );
        }
      }

      // rule_type is derived from whether automation is enabled (D13)
      const derivedRuleType = form.automation !== null ? RuleType.AUTOMATION : RuleType.ALERT;

      const request: RuleCreateExtended = {
        customer_id: customerId,
        rule_type: derivedRuleType,
        name: form.name.trim(),
        description: form.description.trim(),
        trigger_type: form.triggerType,
        event_pattern: form.triggerType === 'event' ? form.eventPattern : undefined,
        schedule_preset: form.triggerType === 'schedule' && !form.useCron ? form.schedulePreset : undefined,
        schedule_expression: resolvedExpression,
        enrichment_prompt_id: form.enrichmentPromptId || undefined,
        summarize: form.summarize,
        itsm: form.itsm || undefined,
        // Webhook targets have no backend yet; save is already blocked when one
        // is present, but strip them so the request matches the backend shape.
        notification_targets: form.notificationTargets.filter(
          (t): t is NotificationTarget => t.channel !== 'webhook',
        ),
        automation: form.automation || undefined,
        resolve_signal: form.resolveSignal || undefined,
        enabled: form.enabled,
        event_examples: form.testEvent && Object.keys(form.testEvent).length > 0 ? [form.testEvent] : [],
        opsitem_strategy: form.opsitemStrategy,
        dedup_field_paths: form.dedupFieldPaths,
      };

      const saved = rule
        ? await eventService.updateEventRule(customerId, rule.rule_id, request)
        : await eventService.createEventRule(customerId, request);

      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  }, [customerId, form, rule, eventService, onSaved, patternValid]);

  const hasSampleEvent = form.testEvent !== null && Object.keys(form.testEvent).length > 0;

  return (
    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      {/* Form column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {rule ? 'Edit Rule' : 'Create Rule'}
        </Typography>
        {fromEvent && <Chip label="From Event" size="small" color="primary" variant="outlined" />}
        {template && <Chip label="From Template" size="small" color="secondary" variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              checked={form.enabled}
              onChange={(e) => setField('enabled', e.target.checked)}
              color="success"
              size="small"
            />
          }
          label={form.enabled ? 'Enabled' : 'Disabled'}
        />
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        {error && (
          <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </InlineAlert>
        )}

        {/* Basic Info */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3, maxWidth: 800 }}>
          <TextField
            fullWidth
            required
            size="small"
            label="Rule Name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={hasAttemptedSave && !!validationErrors.name}
            helperText={(hasAttemptedSave && validationErrors.name) || 'Alphanumeric, underscores, hyphens'}
            placeholder="my-rule-name"
            disabled={!!rule}
            InputLabelProps={{ shrink: true }}
            InputProps={{ sx: { fontFamily: 'monospace' } }}
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="What does this rule do?"
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Numbered vertical flow */}
        <Box sx={{ maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* ① When this happens */}
          <Box>
            <StepHeader number={1} title="When this happens" />
            <StepBox>
              <RuleScheduleSection
                form={form}
                setField={setField}
                hasAttemptedSave={hasAttemptedSave}
                validationErrors={validationErrors}
                onPatternValidityChange={setPatternValid}
              />

              {form.triggerType === 'event' && (
                <Box sx={{ mt: 2.5, pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Test against a sample event
                  </Typography>
                  <TestEventEditor
                    value={form.testEvent}
                    onChange={(v) => setField('testEvent', v)}
                    onValidityChange={setTestEventValid}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
                      onClick={handleTestPattern}
                      disabled={testing || !patternValid || !testEventValid}
                      size="small"
                    >
                      {testing ? 'Testing…' : 'Test match'}
                    </Button>
                    {rule && (
                      <Button
                        variant="text"
                        startIcon={loadingSample ? <CircularProgress size={16} /> : undefined}
                        onClick={handleLoadLatestEvent}
                        disabled={loadingSample}
                        size="small"
                      >
                        {loadingSample ? 'Loading…' : 'Load latest event'}
                      </Button>
                    )}
                    {testResult && <PatternTestResult result={testResult} />}
                  </Box>
                  {testError && (
                    <InlineAlert severity="error" onClose={() => setTestError(null)} sx={{ mt: 1.5 }}>
                      {testError}
                    </InlineAlert>
                  )}
                </Box>
              )}
            </StepBox>
          </Box>

          {/* ②③④ from RuleTargetSection */}
          <RuleTargetSection
            form={form}
            dispatch={dispatch}
            setField={setField}
            hasAttemptedSave={hasAttemptedSave}
            validationErrors={validationErrors}
            saving={saving}
            customerId={customerId}
          />

          {/* ⑤ Incident lifecycle */}
          <Box>
            <StepHeader number={5} title="Action log item lifecycle" />
            <IncidentLifecycleSection
              customerId={customerId}
              opsitemStrategy={form.opsitemStrategy}
              dedupFieldPaths={form.dedupFieldPaths}
              resolveSignal={form.resolveSignal}
              sampleEvent={form.testEvent}
              onStrategyChange={(strategy) => setField('opsitemStrategy', strategy)}
              onDedupChange={(paths) => setField('dedupFieldPaths', paths)}
              onResolveChange={(resolveSignal) => dispatch({ type: 'SET_RESOLVE_SIGNAL', resolveSignal })}
              hasAttemptedSave={hasAttemptedSave}
              validationErrors={validationErrors}
              disabled={saving}
            />
          </Box>
        </Box>
      </Box>

      {/* Action Bar */}
      <FormActionBar
        onSave={handleSave}
        saving={saving}
        saveDisabled={saving || !patternValid}
        saveLabel={rule ? 'Save Changes' : 'Create Rule'}
        size="small"
        onDelete={onDelete}
        deleteLabel="Delete"
        sx={{ px: 3, py: 1.5, mt: 0, pt: 0 }}
      >
        <Button
          variant="outlined"
          onClick={onDiscard}
          disabled={saving}
          size="small"
        >
          Discard
        </Button>
        {rule && (
          <Button
            variant="outlined"
            color={triggerSuccess ? 'success' : 'warning'}
            startIcon={triggering ? <CircularProgress size={14} /> : <TriggerIcon />}
            onClick={handleTriggerNow}
            disabled={triggering || saving}
            size="small"
          >
            {triggering ? 'Triggering...' : triggerSuccess ? 'Triggered' : 'Trigger Now'}
          </Button>
        )}
      </FormActionBar>
      </Box>

      {/* Preview rail */}
      <PreviewRail onPreview={handlePreview} hasSampleEvent={hasSampleEvent} />

      <ConfirmDialog
        open={pendingOverwrite !== null}
        title="Report already exists"
        confirmText="Regenerate"
        confirmColor="warning"
        onCancel={() => setPendingOverwrite(null)}
        onConfirm={handleConfirmOverwrite}
      >
        A report for {pendingOverwrite?.periodLabel} already exists. Regenerate and overwrite it?
      </ConfirmDialog>
    </Box>
  );
};

export default RuleEditor;
